import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import httpMethod from 'services/update2025/httpMethod';

import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";
import { TriStateCheckbox } from "primereact/tristatecheckbox";
import { Dropdown } from "primereact/dropdown";
import { SplitButton } from "primereact/splitbutton";

import { Can } from "configs/CASLContext";
import { deleteConfig, findApplications, findConfigs, reloadConfig } from "services/ConfigService";
import { ConfigContext } from "components/config/ConfigContext";
import { Confirmation } from "components/Confirmation";
import { ConfigEditor } from "components/config/ConfigEditor";
import { Config, create, del, edit, X_TOTAL_COUNT } from "utils/constants";
import { handleError } from "utils/handleError";
import { actionUpdate, actionView } from "utils/action";
import BlockDetail from "components/block-detail/BlockDetail";

export const ConfigList = () => {
    const toast = useRef(null);
    const dt = useRef(null);
    const { t, i18n } = useTranslation(["config", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const configContext = useContext(ConfigContext);
    const { config, resetConfig, setConfig, configs, setConfigs, submitted, setSubmitted, setConfigDialog } = configContext;

    const [applications, setApplications] = useState([]);
    const [reloadItems, setReloadItems] = useState([]);
    const [deleteConfigDialog, setDeleteConfigDialog] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState(null);

    const [loading, setLoading] = useState(false);
    const [checkLoad, setCheckLoad] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        filters: {
            application: { value: "", matchMode: "ilike" },
            profile: { value: "", matchMode: "contains" },
            label: { value: "", matchMode: "contains" },
            key: { value: "", matchMode: "contains" },
            enabled: { value: null, matchMode: "equals" },
        },
    });
    const [globalFilter, setGlobalFilter] = useState(null);

    useEffect(() => {
        findApplications(headers)
            .then((res) => {
                setApplications(res.data);
                setReloadItems(
                    res.data.map((app) => {
                        return {
                            label: app,
                            command: () => reloadApplication(app),
                        };
                    })
                );
            })
            .catch((error) => handleError(toast, t, error));
    }, []);

    useEffect(() => {
        if (checkLoad) {
            loadLazyData();
            setCheckLoad(false);
        }
    }, [lazyParams]);

    useEffect(() => {
        if (submitted) {
            loadLazyData();
            setSubmitted(false);
        }
    }, [submitted]);

    const loadLazyData = () => {
        setLoading(true);
        setConfig(null);
        setSelectedConfig(null);
        findConfigs(headers, lazyParams)
            .then((res) => {
                setTotalRecords(res.headers[X_TOTAL_COUNT] ? parseInt(res.headers[X_TOTAL_COUNT]) : 0);
                const dataRes = res.data.map((c) => {
                    let _config = { ...c };
                    _config.id = `${_config.application}/${_config.profile}/${_config.label}/${_config.key}`;

                    return _config;
                });
                dataRes?.forEach((e, i) => {
                    e.stt = lazyParams.rows * lazyParams.page + 1 + i;
                });
                setConfigs(dataRes);
            })
            .catch((error) => handleError(toast, t, error))
            .finally(() => setLoading(false));
    };

    const reloadApplication = (app) => {
        setLoading(true);
        reloadConfig(headers, app)
            .catch((error) => handleError(toast, t, error))
            .finally(() => setLoading(false));
    };

    const onPage = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onSort = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onFilter = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        _lazyParams["first"] = 0;
        setLazyParams(_lazyParams);
    };

    const openNewDialog = () => {
        resetConfig();
        setSubmitted(false);
        setConfigDialog(true);
    };

    const openEditDialog = () => {
        setConfig({ ...selectedConfig, action: actionUpdate });
        setSubmitted(false);
        setConfigDialog(true);
    };

    const openViewDialog = (config) => {
        setConfig({ ...config, action: actionView });
        setSubmitted(false);
        setConfigDialog(true);
    };

    const confirmDeleteSelectedConfig = () => {
        setDeleteConfigDialog(true);
    };

    const hideDeleteConfigDialog = () => {
        setDeleteConfigDialog(false);
    };

    const deleteConfigAction = () => {
        deleteConfig(headers, selectedConfig.application, selectedConfig.profile, selectedConfig.label, selectedConfig.key)
            .then(() => {
                setLazyParams({
                    ...lazyParams,
                    page: 0,
                    first: 0,
                });
                setDeleteConfigDialog(false);
                resetConfig();
                setSelectedConfig(null);
                loadLazyData();
                toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.deleted", { ns: "common", object: t("config") }), life: 3000 });
            })
            .catch((error) => handleError(toast, t, error));
    };

    const checkboxFilterTemplate = (options) => {
        return (
            <div className="flex align-items-center justify-content-center">
                <TriStateCheckbox value={options.value} onChange={(e) => options.filterApplyCallback(e.value)} />
            </div>
        );
    };

    const textFilterTemplate = (options) => {
        return (
            <div className="flex align-items-center justify-content-center">
                <InputText value={options.value} onChange={(e) => options.filterApplyCallback(e.target?.value || "")} />
            </div>
        );
    };

    const applicationFilterTemplate = (options) => {
        return <Dropdown value={options.value} options={applications} onChange={(e) => options.filterApplyCallback(e.value)} />;
    };

    const actionTemplate = () => {
        return (
            <div className="grid">
                <div className="col-12 tex-center mt-2" style={{ display: "flex", justifyContent: "center" }}>
                    {/* tạo mới */}
                    <Button label={t("new", { ns: "common" })} icon="pi pi-plus" className="p-button mr-2" onClick={openNewDialog} />
                    {/* sửa */}
                    <Button disabled={!selectedConfig} label={t("edit", { ns: "common" })} icon="pi pi-pencil" className="p-button mr-2" onClick={openEditDialog} />
                    {/* xóa */}
                    <Button disabled={!selectedConfig} label={t("delete", { ns: "common" })} icon="pi pi-trash" className="p-button-danger p-button mr-2" onClick={confirmDeleteSelectedConfig} />
                </div>
            </div>
        );
    };

    const headerTemplate = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">{t("config.list")}</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder={t("search", { ns: "common" })} />
            </span>
        </div>
    );

    const checkboxBodyTemplate = (rowData) => {
        return (
            <>
                <div className="flex align-items-center justify-content-center">
                    <Checkbox checked={rowData.enabled} readOnly />
                </div>
            </>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center justify-content-center actions">
                <Button icon="pi pi-eye" className="p-button-rounded" tooltip={t("view", { ns: "common" })} onClick={() => openViewDialog(rowData)} />
            </div>
        );
    };

    const paginatorTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
        RowsPerPageDropdown: (options) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 20, value: 20 },
                { label: 30, value: 30 },
                { label: 50, value: 50 },
            ];

            return (
                <React.Fragment>
                    <div style={{ width: "140px", display: "inline-block" }}>
                        <span className="mx-1">{t("show", { ns: "common" })}: </span>
                        <Dropdown value={options.value} options={dropdownOptions} onChange={options.onChange} />
                    </div>
                </React.Fragment>
            );
        },
        CurrentPageReport: (options) => {
            return <span style={{ width: "calc(100% - 490px)", textAlign: "center", display: "inline-block" }}>{t("total", { ns: "common", total: options.totalRecords })}</span>;
        },
    };

    const searchOption = (
        <div className="grid">
            {/* Ứng dụng */}
            <div className="col-12 md:col-4">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "130px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="application"
                    >
                        {t("config.application")}
                    </label>
                    <div className="col">
                        <InputText
                            style={{
                                width: "100%",
                            }}
                            id="application"
                            name="application"
                            onChange={(e) => {
                                setCheckLoad(false);
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        application: {
                                            ...lazyParams.filters.application,
                                            value: e.target.value,
                                        },
                                    },
                                });
                            }}
                        ></InputText>
                    </div>
                </div>
            </div>

            <div className="col-12 md:col-8 text-right">
                <div className="grid custom-form">
                    <div className="col-12">
                        <label
                            style={{
                                // width: "130px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 0px",
                            }}
                        >
                            {" "}
                        </label>
                    </div>
                    <div className="col-12" style={{ margin: "5px 0px 0px 0px" }}>
                        <Button
                            label={t("search", { ns: "common" })}
                            icon="pi pi-search"
                            onClick={(e) => {
                                setCheckLoad(true);
                                setLazyParams({
                                    ...lazyParams,
                                    first: 0,
                                    page: 0,
                                });
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="grid crud">
            <div className="col-12">
                <div className="card">
                    <div className="grid">
                        <div className="col-12">
                            <h4 className="text-center font-bold">{t("config.title")?.toUpperCase()}</h4>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("search", { ns: "common" })}>{searchOption}</BlockDetail>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("config.list")}>
                                <Toast ref={toast} />
                                <DataTable
                                    ref={dt}
                                    value={configs}
                                    selectionMode="single"
                                    selection={selectedConfig}
                                    onSelectionChange={(e) => {
                                        setSelectedConfig(e.value);
                                    }}
                                    dataKey="id"
                                    className="datatable-responsive p-datatable-sm"
                                    // globalFilter={globalFilter}
                                    // header={headerTemplate}
                                    showGridlines
                                    lazy
                                    paginator
                                    rows={lazyParams.rows}
                                    // rowsPerPageOptions={[10, 20, 50]}
                                    first={lazyParams.first}
                                    totalRecords={totalRecords}
                                    onPage={onPage}
                                    onSort={onSort}
                                    sortField={lazyParams.sortField}
                                    sortOrder={lazyParams.sortOrder}
                                    onFilter={onFilter}
                                    filters={lazyParams.filters}
                                    loading={loading}
                                    paginatorClassName="justify-content-start"
                                    paginatorTemplate={paginatorTemplate}
                                    emptyMessage="Không có dữ liệu"
                                    scrollable
                                >
                                    <Column field="stt" header={t("stt", { ns: "common" })} style={{ maxWidth: "50px" }} />
                                    <Column field="application" header={t("application")} sortable filter showFilterMatchModes={false} filterElement={applicationFilterTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="profile" header={t("profile")} sortable filter showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="label" header={t("label")} sortable filter showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="key" header={t("key")} sortable filter showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column
                                        field="enabled"
                                        header={t("enabled")}
                                        dataType="boolean"
                                        sortable
                                        filter
                                        filterElement={checkboxFilterTemplate}
                                        body={checkboxBodyTemplate}
                                        headerStyle={{ width: "10rem" }}
                                        style={{ minWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }}
                                    />
                                    <Column body={actionBodyTemplate} style={{ maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                </DataTable>
                            </BlockDetail>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12">{actionTemplate()}</div>
                    </div>

                    <Confirmation
                        visible={deleteConfigDialog}
                        hideAction={hideDeleteConfigDialog}
                        processAction={deleteConfigAction}
                        confirmMessage={
                            selectedConfig && (
                                <span>
                                    {t("confirm.delete", { ns: "common", object: t("config") })} <b>{selectedConfig.label}</b>?
                                </span>
                            )
                        }
                    />
                    <ConfigEditor />
                </div>
            </div>
        </div>
    );
};
